import { useConfirmRejectForm } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/hooks/useConfirmRejectForm';
import { useTranslation } from '@/i18n';
import {
  Bottom,
  Form,
  FormRadioButton,
  ListView,
  ListViewBody,
  ListViewLabel,
  Loading,
  Top,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useMemo } from 'react';
import { CommentField } from '../components/CommentField';
import { useOptionsReject } from '../hooks/useOptionsReject';
import { ConfirmRejectForm, ContentsProps } from '../types';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

export const InnerContents: FC<ContentsProps> = ({
  reportId,
  onCloseDetailDrawer,
}) => {
  const { options } = useOptionsReject(reportId, onCloseDetailDrawer);
  const { methods } = useConfirmRejectForm();
  const { control } = methods;
  const { t } = useTranslation();
  const renderDestination = useMemo(() => {
    if (options?.length === 0) return null;
    if (options?.length === 1) {
      return (
        <div>
          <span className={cx('destination-label')}>{options[0]?.label}</span>
          <span className={cx('destination-caption')}>
            {options[0]?.caption?.children}
          </span>
        </div>
      );
    }
    return (
      <FormRadioButton
        vertical
        inputId='rejectDestination'
        control={control}
        options={options || []}
        name='rejectDestination'
        testId='rejectDestination'
      />
    );
  }, [control, options]);

  return (
    <Form<ConfirmRejectForm>
      formMethod={methods}
      className={cx('form-container')}
    >
      <ListView>
        <Top>
          <ListViewLabel key='left'>
            {t('approving_invoice_reports.reject_modal.destination')}
          </ListViewLabel>
          <ListViewBody key='right' className={cx('list-view-body')}>
            {renderDestination}
          </ListViewBody>
        </Top>

        <Bottom>
          <ListViewLabel key='left'>
            {t('approving_invoice_reports.reject_modal.comment')}
          </ListViewLabel>
          <ListViewBody
            key='right'
            className={cx('list-view-body', 'comment-field')}
          >
            <CommentField control={control} />
          </ListViewBody>
        </Bottom>
      </ListView>
    </Form>
  );
};

const FallBackLoading = () => {
  return (
    <div className={cx('loading-container')}>
      <Loading size='large' color='default' />
    </div>
  );
};

export const Contents: FC<ContentsProps> = memo((props) => {
  return (
    <Suspense fallback={<FallBackLoading />}>
      <InnerContents {...props} />
    </Suspense>
  );
});
Contents.displayName = 'Contents';
